// Aggregation for the Group By Stations history page. Everything here works on
// the rows returned by cms_get_daywise_by_stationwise_stat_group; values are
// only summed and grouped, never recalculated, so every chart and table adds up
// to the same totals as the per-day station report.
import { bucketKey, bucketLabel, daysBetween, listDays } from "./analyticsMetrics";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const METRICS = {
  revenue: { key: "final", label: "Final revenue" },
  energy: { key: "energy", label: "Energy" },
  sessions: { key: "sessions", label: "Sessions" },
  duration: { key: "duration", label: "Charging time" },
};

// one record per station per day, same field mapping as the original report
export const toRecords = (apiData) =>
  (Array.isArray(apiData) ? apiData : []).map((item, i) => ({
    id: `${item.SessionDate || "unknown"}-${item.StationID || "unknown"}-${i}`,
    date: item.SessionDate?.split("T")[0] || "unknown",
    stationId: String(item.StationID || "unknown"),
    sessions: num(item.TotalSessions ?? 0),
    gst: num(item.TotalGSTAmount ?? 0),
    base: num(item.TotalCost ?? 0),
    final: num(item.TotalFinalCost ?? 0),
    energy: num(item.TotalEnergyKWh ?? 0),
    duration: num(item.TotalDurationSeconds ?? 0),
  }));

const empty = () => ({ sessions: 0, gst: 0, base: 0, final: 0, energy: 0, duration: 0 });
const add = (acc, r) => {
  acc.sessions += r.sessions;
  acc.gst += r.gst;
  acc.base += r.base;
  acc.final += r.final;
  acc.energy += r.energy;
  acc.duration += r.duration;
  return acc;
};

export const totalsOf = (records) => {
  const t = records.reduce(add, empty());
  t.stations = new Set(records.filter((r) => r.sessions > 0).map((r) => r.stationId)).size;
  t.days = new Set(records.map((r) => r.date)).size;
  return t;
};

// The time axis covers every day of the selected range (days without records
// are 0), widened if the server returned records outside it.
export const timeBuckets = (records, range, granularity) => {
  const dated = records.filter((r) => r.date !== "unknown");
  let from = range.fromDate;
  let to = range.toDate;
  dated.forEach((r) => {
    if (r.date < from) from = r.date;
    if (r.date > to) to = r.date;
  });
  if (!from || !to || from > to) return [];
  // guard against a malformed date producing an endless axis
  if (daysBetween(from, to) > 3700) return [];
  const keys = [];
  listDays(from, to).forEach((d) => {
    const k = bucketKey(d, granularity);
    if (keys[keys.length - 1] !== k) keys.push(k);
  });
  return keys.map((k) => ({ bucket: k, label: bucketLabel(k, granularity) }));
};

// [{ bucket, label, sessions, gst, base, final, energy, duration }]
export const trendSeries = (records, range, granularity) => {
  const buckets = timeBuckets(records, range, granularity);
  const byBucket = new Map(buckets.map((b) => [b.bucket, { ...b, ...empty() }]));
  records.forEach((r) => {
    if (r.date === "unknown") return;
    const row = byBucket.get(bucketKey(r.date, granularity));
    if (row) add(row, r);
  });
  return [...byBucket.values()];
};

// one value per bucket per station: [{ bucket, label, [stationId]: value }]
export const stationSeries = (records, range, granularity, stationIds, metricKey) => {
  const buckets = timeBuckets(records, range, granularity);
  const byBucket = new Map(
    buckets.map((b) => [b.bucket, { ...b, ...Object.fromEntries(stationIds.map((id) => [id, 0])) }])
  );
  const wanted = new Set(stationIds);
  records.forEach((r) => {
    if (r.date === "unknown" || !wanted.has(r.stationId)) return;
    const row = byBucket.get(bucketKey(r.date, granularity));
    if (row) row[r.stationId] += r[metricKey];
  });
  return [...byBucket.values()];
};

// per-station totals, highest final revenue first
export const stationTotals = (records) => {
  const map = new Map();
  records.forEach((r) => {
    if (!map.has(r.stationId)) map.set(r.stationId, { stationId: r.stationId, days: 0, ...empty() });
    const t = map.get(r.stationId);
    add(t, r);
    t.days += 1;
  });
  return [...map.values()].sort((a, b) => b.final - a.final);
};

// per-date summary with its station rows, newest date first
export const dateGroups = (records) => {
  const map = new Map();
  records.forEach((r) => {
    if (!map.has(r.date)) map.set(r.date, { date: r.date, stations: [], ...empty() });
    const g = map.get(r.date);
    g.stations.push(r);
    add(g, r);
  });
  return [...map.values()]
    .map((g) => ({ ...g, stations: [...g.stations].sort((a, b) => b.final - a.final) }))
    .sort((a, b) => (a.date === "unknown" ? 1 : b.date === "unknown" ? -1 : b.date.localeCompare(a.date)));
};

// whole hours and minutes, like the original report ("3h 25m")
export const formatHours = (seconds) => {
  const s = num(seconds);
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

const longDate = new Intl.DateTimeFormat("en-US", { weekday: "short", year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
export const formatReportDate = (key) => (key === "unknown" ? "Unknown date" : longDate.format(new Date(`${key}T00:00:00Z`)));
